(() => {
  'use strict';

  const STORAGE_KEYS = {
    commune: 'pato-wa-order-commune',
    address: 'pato-wa-order-address',
    name: 'pato-wa-order-name',
    payment: 'pato-wa-order-payment',
    observations: 'pato-wa-order-observations',
    dwelling: 'pato-wa-order-dwelling',
    apartment: 'pato-wa-order-apartment',
  };
  const pesoFormat = new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    maximumFractionDigits: 0,
  });
  let refreshQueued = false;

  function moneyFromCents(value) {
    const amount = Number(value || 0);
    return pesoFormat.format(amount / 100);
  }

  function getRootPath() {
    const root = window.Shopify?.routes?.root || '/';
    return root.endsWith('/') ? root : `${root}/`;
  }

  async function getCart() {
    const response = await fetch(`${getRootPath()}cart.js`, {
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error('No se pudo consultar el carrito de Shopify.');
    return response.json();
  }

  function getShipping(wrapper) {
    const select = wrapper.querySelector('[data-wa-comuna]');
    const option = select?.selectedOptions?.[0];
    if (!select || !select.value || !option || option.disabled) return null;
    const price = Number(option.dataset.price);
    if (!Number.isFinite(price) || price < 0) return null;
    return { commune: select.value, pesos: price };
  }

  function getPayment(wrapper) {
    return wrapper.querySelector('[data-wa-pago]')?.value || '';
  }

  function getSurchargeCents(productTotalCents, shippingCents, payment) {
    if (payment !== 'Tarjeta de crédito') return 0;
    return Math.round((productTotalCents + shippingCents) * 0.03);
  }

  function updateDisplay(wrapper, cart) {
    const shipping = getShipping(wrapper);
    const payment = getPayment(wrapper);
    const shippingNode = wrapper.querySelector('[data-wa-shipping]');
    const totalNode = wrapper.querySelector('[data-wa-total]');
    const surchargeNode = wrapper.querySelector('[data-wa-recargo]');
    const surchargeRow = wrapper.querySelector('[data-wa-recargo-row]');
    const productTotal = Number(cart?.total_price || 0);
    const shippingCents = shipping ? shipping.pesos * 100 : 0;
    const surchargeCents = getSurchargeCents(productTotal, shippingCents, payment);

    if (shippingNode) shippingNode.textContent = shipping ? pesoFormat.format(shipping.pesos) : 'Selecciona tu comuna';
    if (surchargeNode) surchargeNode.textContent = moneyFromCents(surchargeCents);
    if (surchargeRow) surchargeRow.hidden = payment !== 'Tarjeta de crédito' || !shipping;
    if (totalNode) {
      totalNode.textContent = shipping
        ? moneyFromCents(productTotal + shippingCents + surchargeCents)
        : 'Selecciona tu comuna';
    }
  }

  function restoreInputs(wrapper) {
    const select = wrapper.querySelector('[data-wa-comuna]');
    const address = wrapper.querySelector('[data-wa-direccion]');
    const name = wrapper.querySelector('[data-wa-nombre]');
    const payment = wrapper.querySelector('[data-wa-pago]');
    const observations = wrapper.querySelector('[data-wa-observaciones]');
    const dwelling = wrapper.querySelector('[data-wa-tipo-domicilio]');
    const apartment = wrapper.querySelector('[data-wa-depto]');
    try {
      const savedCommune = sessionStorage.getItem(STORAGE_KEYS.commune);
      const savedAddress = sessionStorage.getItem(STORAGE_KEYS.address);
      const savedName = sessionStorage.getItem(STORAGE_KEYS.name);
      const savedPayment = sessionStorage.getItem(STORAGE_KEYS.payment);
      const savedObservations = sessionStorage.getItem(STORAGE_KEYS.observations);
      if (select && savedCommune && [...select.options].some((option) => option.value === savedCommune && !option.disabled)) {
        select.value = savedCommune;
      }
      if (address && savedAddress && !address.value) address.value = savedAddress;
      if (name && savedName && !name.value) name.value = savedName;
      if (payment && savedPayment && [...payment.options].some((option) => option.value === savedPayment)) payment.value = savedPayment;
      if (observations && savedObservations && !observations.value) observations.value = savedObservations;
      const savedDwelling = sessionStorage.getItem(STORAGE_KEYS.dwelling);
      const savedApartment = sessionStorage.getItem(STORAGE_KEYS.apartment);
      if (dwelling && savedDwelling && [...dwelling.options].some(option => option.value === savedDwelling)) dwelling.value = savedDwelling;
      if (apartment && savedApartment && !apartment.value) apartment.value = savedApartment;
    } catch (_) {
      // Continue normally when browser storage is unavailable.
    }
    updateDwelling(wrapper);
  }

  function updateDwelling(wrapper) {
    const type = wrapper.querySelector('[data-wa-tipo-domicilio]')?.value || '';
    const field = wrapper.querySelector('[data-wa-depto-field]');
    const apartment = wrapper.querySelector('[data-wa-depto]');
    if (field) field.hidden = type !== 'Departamento';
    if (apartment) apartment.required = type === 'Departamento';
    wrapper.querySelectorAll('[data-wa-dwelling-choice]').forEach((button) => {
      const selected = button.dataset.waDwellingChoice === type;
      button.classList.toggle('is-selected', selected);
      button.setAttribute('aria-pressed', String(selected));
    });
  }

  function setButtonLoading(button, loading) {
    const label = button.querySelector('[data-wa-submit-label]');
    if (loading) {
      button.dataset.originalLabel = label?.textContent || 'Confirmar pedido por WhatsApp';
      if (label) label.textContent = 'Preparando pedido…';
      button.disabled = true;
    } else {
      if (label) label.textContent = button.dataset.originalLabel || 'Confirmar pedido por WhatsApp';
      button.disabled = false;
    }
  }

  async function handleSubmit(button) {
    const wrapper = button.closest('[data-wa-order]');
    if (!wrapper || button.disabled) return;

    const select = wrapper.querySelector('[data-wa-comuna]');
    const addressInput = wrapper.querySelector('[data-wa-direccion]');
    const nameInput = wrapper.querySelector('[data-wa-nombre]');
    const paymentInput = wrapper.querySelector('[data-wa-pago]');
    const observationsInput = wrapper.querySelector('[data-wa-observaciones]');
    const dwellingInput = wrapper.querySelector('[data-wa-tipo-domicilio]');
    const apartmentInput = wrapper.querySelector('[data-wa-depto]');
    const shipping = getShipping(wrapper);
    const address = (addressInput?.value || '').trim();
    const customerName = (nameInput?.value || '').trim();
    const payment = getPayment(wrapper);
    const observations = (observationsInput?.value || '').trim();
    const dwelling = dwellingInput?.value || '';
    const apartment = (apartmentInput?.value || '').trim();

    if (!customerName) {
      window.alert('Ingresa el nombre y apellido de quien realiza el pedido.');
      nameInput?.focus();
      return;
    }
    if (!payment) {
      window.alert('Selecciona el medio de pago.');
      paymentInput?.focus();
      return;
    }
    if (!shipping) {
      window.alert('Selecciona una comuna con cobertura para continuar.');
      select?.focus();
      return;
    }
    if (!address) {
      window.alert('Ingresa la dirección de entrega para continuar.');
      addressInput?.focus();
      return;
    }

    if (!dwelling) {
      window.alert('Selecciona si el domicilio es casa o departamento.');
      dwellingInput?.focus();
      return;
    }
    if (dwelling === 'Departamento' && !apartment) {
      window.alert('Ingresa el número de departamento.');
      apartmentInput?.focus();
      return;
    }

    setButtonLoading(button, true);
    try {
      const cart = await getCart();
      if (!Array.isArray(cart.items) || cart.items.length === 0) {
        window.alert('Tu carrito está vacío. Agrega productos antes de confirmar el pedido.');
        return;
      }

      const phone = (wrapper.dataset.whatsappPhone || '').replace(/\D/g, '');
      if (!phone) throw new Error('Falta configurar el número de WhatsApp de destino.');

      const money = moneyFromCents;
      const lineItems = cart.items.map((item) => {
        const variant = item.variant_title && item.variant_title !== 'Default Title'
          ? ` — ${item.variant_title}`
          : '';
        return `▪️ ${item.product_title || item.title || 'Producto'}${variant}\nCantidad: ${item.quantity}\nSubtotal: ${money(item.final_line_price ?? item.line_price)}\n`;
      });

      const subtotalOriginal = Number(cart.original_total_price ?? cart.items_subtotal_price ?? cart.total_price ?? 0);
      const discounts = Number(cart.total_discount || 0);
      const productTotal = Number(cart.total_price || 0);
      const shippingCents = shipping.pesos * 100;
      const surchargeCents = getSurchargeCents(productTotal, shippingCents, payment);
      const total = productTotal + shippingCents + surchargeCents;

      let message = '🛒 *PEDIDO PARA CONFIRMAR*\n\n';
      message += `👤 *Cliente:* ${customerName}\n`;
      message += `💳 *Medio de pago elegido:* ${payment}\n`;
      message += lineItems.join('\n');
      message += `\n📍 *Comuna:* ${shipping.commune}\n`;
      message += `🏠 *Dirección:* ${address}\n`;
      message += `🏡 *Tipo de domicilio:* ${dwelling}\n`;
      if (dwelling === 'Departamento') message += `🏢 *Departamento:* ${apartment}\n`;
      message += '\n';
      message += `🧀 *Subtotal productos:* ${money(subtotalOriginal)}\n`;
      if (discounts > 0) message += `🏷️ *Descuentos:* -${money(discounts)}\n`;
      message += `📦 *Total productos con descuentos:* ${money(productTotal)}\n`;
      message += `🚚 *Costo de despacho:* ${pesoFormat.format(shipping.pesos)}\n`;
      if (surchargeCents > 0) message += `💳 *Recargo tarjeta de crédito (3%):* ${money(surchargeCents)}\n`;
      message += `💰 *TOTAL A PAGAR: ${money(total)}*\n\n`;
      if (observations) message += `📝 *Observaciones:* ${observations}\n\n`;
      message += 'Hola, quisiera confirmar este pedido.';

      const link = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
      window.location.href = link;
    } catch (error) {
      console.error('[whatsapp-order] No se pudo preparar el pedido:', error);
      window.alert('No se pudo preparar el pedido. Revisa tu conexión e inténtalo nuevamente.');
    } finally {
      setButtonLoading(button, false);
      // The navigation to WhatsApp normally happens here. Keep the restored label if it does not.
    }
  }

  async function refreshAllWrappers() {
    const wrappers = document.querySelectorAll('[data-wa-order]');
    if (!wrappers.length) return;
    try {
      const cart = await getCart();
      wrappers.forEach((wrapper) => updateDisplay(wrapper, cart));
    } catch (error) {
      console.warn('[whatsapp-order] No se pudo actualizar el total del carrito:', error);
    }
  }

  function queueRefresh() {
    if (refreshQueued) return;
    refreshQueued = true;
    window.setTimeout(() => {
      refreshQueued = false;
      refreshAllWrappers();
    }, 80);
  }

  function initializeWrappers() {
    document.querySelectorAll('[data-wa-order]').forEach((wrapper) => {
      if (wrapper.dataset.waInitialized === 'true') return;
      wrapper.dataset.waInitialized = 'true';
      restoreInputs(wrapper);
      updateDwelling(wrapper);
      updateDisplay(wrapper, null);
    });
    queueRefresh();
  }

  document.addEventListener('change', (event) => {
    if (event.target.matches('[data-wa-comuna]')) {
      try { sessionStorage.setItem(STORAGE_KEYS.commune, event.target.value); } catch (_) {}
      refreshAllWrappers();
    }
    if (event.target.matches('[data-wa-tipo-domicilio]')) {
      try { sessionStorage.setItem(STORAGE_KEYS.dwelling, event.target.value); } catch (_) {}
      updateDwelling(event.target.closest('[data-wa-order]'));
    }
    if (event.target.matches('[data-wa-pago]')) {
      try { sessionStorage.setItem(STORAGE_KEYS.payment, event.target.value); } catch (_) {}
      refreshAllWrappers();
    }
  });

  document.addEventListener('input', (event) => {
    if (event.target.matches('[data-wa-depto]')) {
      try { sessionStorage.setItem(STORAGE_KEYS.apartment, event.target.value); } catch (_) {}
    }
    if (event.target.matches('[data-wa-direccion]')) {
      try { sessionStorage.setItem(STORAGE_KEYS.address, event.target.value); } catch (_) {}
    }
    if (event.target.matches('[data-wa-nombre]')) {
      try { sessionStorage.setItem(STORAGE_KEYS.name, event.target.value); } catch (_) {}
    }
    if (event.target.matches('[data-wa-observaciones]')) {
      try { sessionStorage.setItem(STORAGE_KEYS.observations, event.target.value); } catch (_) {}
    }
  });

  document.addEventListener('click', (event) => {
    const choice = event.target.closest('[data-wa-dwelling-choice]');
    if (choice) {
      const wrapper = choice.closest('[data-wa-order]');
      const select = wrapper?.querySelector('[data-wa-tipo-domicilio]');
      if (select) {
        select.value = choice.dataset.waDwellingChoice;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return;
    }
    const button = event.target.closest('[data-wa-submit]');
    if (button) {
      event.preventDefault();
      handleSubmit(button);
    }
  });

  const observer = new MutationObserver((mutations) => {
    let cartChanged = false;
    for (const mutation of mutations) {
      const target = mutation.target instanceof Element ? mutation.target : mutation.target.parentElement;
      if (target?.closest('[data-wa-order]')) continue;
      const cartArea = target?.closest('.cart-drawer__items, .cart-totals, cart-items-component');
      if (cartArea) cartChanged = true;
      for (const node of mutation.addedNodes || []) {
        if (!(node instanceof Element)) continue;
        if (node.matches('[data-wa-order]') || node.querySelector('[data-wa-order]')) initializeWrappers();
      }
    }
    if (cartChanged) queueRefresh();
  });

  function start() {
    initializeWrappers();
    observer.observe(document.body, { childList: true, subtree: true, characterData: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
